import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { submitContactMessage } from '../services/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await submitContactMessage(formData);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError('Failed to send message. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Contact Us | Close Now Lending - Houston Real Estate Lender"
        description="Contact Close Now Lending for fast real estate investment loans. Located at 4601 Yale St, Houston, TX 77018. Call 903-476-2937 for hard money, fix and flip, DSCR, and commercial loans."
        keywords="contact Close Now Lending, Houston hard money lender phone, real estate loan inquiry, get a loan quote, hard money loan application, fix and flip loan Houston, DSCR loan application"
        url="/contact"
      />
      <Header />

      {/* Hero */}
      <section className="bg-slate-900 pt-28 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
              Get in <span className="text-blue-400">Touch</span>
            </h1>
            <p className="text-lg text-gray-300 max-w-xl">
              Ready to discuss your next real estate investment? Our team is standing by to help you find the right financing solution.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-7xl mx-auto">

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>

              {submitted ? (
                <div data-testid="contact-success" className="bg-green-50 border border-green-200 rounded-xl p-8 text-center">
                  <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600 mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <Button onClick={() => setSubmitted(false)} variant="outline" data-testid="send-another-btn">Send Another Message</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-gray-700">Full Name *</Label>
                      <Input
                        id="name"
                        data-testid="contact-name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="John Smith"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address *</Label>
                      <Input
                        id="email"
                        data-testid="contact-email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="john@example.com"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number</Label>
                    <Input
                      id="phone"
                      data-testid="contact-phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-gray-700">Message *</Label>
                    <textarea
                      id="message"
                      data-testid="contact-message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Tell us about your project or financing needs..."
                      className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                  </div>

                  {error && <p className="text-red-600 text-sm">{error}</p>}

                  <p className="text-xs text-gray-500 leading-relaxed">
                    By submitting this form, you agree to be contacted by Close Now Lending Corp via call, email, and text for financial services. To opt out of text messages, you can reply 'stop' at any time or reply 'help' for assistance. You can also select the unsubscribe link in the emails. Message and data rates may apply. Message frequency may vary. <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a> | <a href="/terms" className="text-blue-600 underline">Terms of Service</a>
                  </p>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              )}
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-5">
                  <a href="tel:+19034762937" className="flex items-start gap-4 group" data-testid="contact-phone-link">
                    <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-colors">
                      <Phone className="h-5 w-5 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Phone</p>
                      <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">903-476-2937</p>
                    </div>
                  </a>
                  <a href="mailto:info@closenowlending.com" className="flex items-start gap-4 group">
                    <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-blue-600 transition-colors">
                      <Mail className="h-5 w-5 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">info@closenowlending.com</p>
                    </div>
                  </a>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="font-semibold text-gray-900">4601 Yale St<br />Houston, TX 77018</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Business Hours</p>
                      <p className="font-semibold text-gray-900">Mon - Fri: 8:00 AM - 6:00 PM</p>
                      <p className="text-sm text-gray-500">Sat - Sun: By Appointment</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick CTA */}
              <div className="bg-slate-900 rounded-xl p-6">
                <h3 className="text-white font-bold text-lg mb-2">Need Financing Fast?</h3>
                <p className="text-gray-300 text-sm mb-4">We can pre-qualify you in 24 hours and close in as few as 10 days.</p>
                <a href="tel:+19034762937">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                    <Phone className="h-4 w-4 mr-2" />
                    Call Now: 903-476-2937
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Embed */}
      <section className="bg-gray-50">
        <div className="container mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Visit Our Office</h2>
          <div className="rounded-xl overflow-hidden shadow-lg max-w-5xl mx-auto" data-testid="contact-map">
            <iframe
              title="Close Now Lending Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3462.0!2d-95.4!3d29.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s4601+Yale+St+Houston+TX+77018!5e0!3m2!1sen!2sus!4v1"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
